import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import type { ConversationKind } from '../lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { cn } from '../lib/utils'

/**
 * NewGroup creates a group or broadcast room from the user's existing
 * contacts.
 *
 * Unlike AddContact, this never talks to an agent server — every agent picked
 * here is already a contact, so the only thing this endpoint does is
 * conversations.Create plus a membership row per agent. The kind choice is
 * the one decision that changes what the room *is*: a group routes each
 * message to whoever is @mentioned, a broadcast sends every message to
 * everyone and is meant for comparing answers side by side.
 */
export function NewGroup({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<ConversationKind>('group')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: api.listAgents,
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      setTitle('')
      setKind('group')
      setSelected(new Set())
    }
  }, [open])

  const create = useMutation({
    mutationFn: () =>
      api.createConversation({
        kind,
        agent_ids: [...selected],
        title: title.trim() || undefined,
      }),
    onSuccess: async (conv) => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] })
      onClose()
      navigate(`/c/${conv.id}`)
    },
  })

  const message =
    create.error instanceof ApiError ? create.error.message : create.error ? 'Something went wrong.' : ''

  const kindOptions = [
    { value: 'group' as const, label: 'Group', hint: '@mention who should answer' },
    { value: 'broadcast' as const, label: 'Broadcast', hint: 'every message reaches everyone' },
  ]

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="new-group-overlay">
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>Pick two or more agents you have already added.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Input
            placeholder="Room name (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="group-title"
          />

          <div className="flex gap-2">
            {kindOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  'flex flex-1 flex-col rounded-md border p-3 text-left',
                  kind === opt.value ? 'border-primary bg-primary/5' : 'border-border',
                )}
                onClick={() => setKind(opt.value)}
                data-testid={`kind-${opt.value}`}
              >
                <span className="text-sm font-medium">{opt.label}</span>
                <span className="text-xs text-muted-foreground">{opt.hint}</span>
              </button>
            ))}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">
              {agents.length === 0 ? 'Add a contact first — there is nobody to put in a room yet.' : 'Members'}
            </p>
            <div className="flex flex-col gap-2">
              {agents.map((agent) => {
                const checked = selected.has(agent.id)
                return (
                  <button
                    key={agent.id}
                    type="button"
                    className={cn(
                      'flex items-center gap-3 rounded-md border p-3 text-left',
                      checked ? 'border-primary bg-primary/5' : 'border-border',
                    )}
                    onClick={() =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        if (next.has(agent.id)) next.delete(agent.id)
                        else next.add(agent.id)
                        return next
                      })
                    }
                    data-testid="group-member"
                  >
                    <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                    <span className="text-sm">{agent.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {message && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{message}</div>
        )}

        <Button
          className="w-full"
          onClick={() => create.mutate()}
          disabled={selected.size < 2 || create.isPending}
          data-testid="create-group"
        >
          {create.isPending ? 'Creating…' : `Create ${kind} with ${selected.size} agent${selected.size === 1 ? '' : 's'}`}
        </Button>

        <Button className="w-full" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  )
}
